import React, { useState } from "react";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { LucideChevronDown, LucidePencilLine, LucidePlus } from "lucide-react";
import type { ShopBannerEntity, ShopEntity, ShopSectionEntity } from "../../../../../../domain/types/company-shop";
import type { ProductEntity } from "../../../../../../domain/types/product";
import {
  SECTION_COLUMN_COUNT_OPTIONS,
  SECTION_GRID_ROWS,
  SECTION_LAYOUT_TYPE_LABELS,
  SECTION_TYPE_LABELS,
  isColumnCountMeaningful,
} from "../../constants";
import { renumberSections } from "../../utils";
import { SectionProductPickerModal } from "../modals/SectionProductPickerModal";
import { DragHandleIcon, Field, Notice, Segmented, SectionTextFields, SettingsCard, TextField, ToggleSwitch } from "../ui/SettingsUI";

/*
  메인 화면 탭 (2026-10-26 정리).
  예전에는 '섹션 순서' 카드와 섹션별 카드(배너 · 띠배너 · 최근 둘러본 · 베스트셀러 · 전체 상품 · 포트폴리오 ·
  상품 리스트)가 따로 있어서 같은 사용 여부를 두 곳에서 켜고 껐다. 이제 쇼핑몰 화면 순서 그대로의 목록 하나에서
  끌어서 순서를 바꾸고, 스위치로 켜고, 펼쳐서 세부 설정을 한다.
*/

interface MainSectionsSettingsProps {
  sections: ShopSectionEntity[];
  onReorder: (sections: ShopSectionEntity[]) => void;
  onToggleEnabled: (section: ShopSectionEntity) => void;
  onPatchSection: (sectionId: number, patch: Partial<ShopSectionEntity>) => void;
  expandedSectionId: number | null;
  onExpand: (sectionId: number | null) => void;
  isPersistable: boolean;

  shopEntityData: ShopEntity | null;
  setShopEntityData: React.Dispatch<React.SetStateAction<ShopEntity | null>>;

  bannersData: ShopBannerEntity[] | null;
  onAddBanner: () => void;
  onEditBanner: (bannerId: number) => void;

  stripBannerPreviewUrl: string | null;
  onPickStripBanner: (file: File) => void;
  onDeleteStripBanner: () => Promise<void>;

  productsData: ProductEntity[] | null;
  /** 쇼핑몰에 실제로 보이는 상품 ID. 모르면 null (표시 안 함) */
  visibleProductIds: Set<number> | null;
  onEditBestSellers: () => void;

  onCreateProductList: () => Promise<void>;
  onDeleteProductList: (section: ShopSectionEntity) => Promise<void>;
}

const sectionLabel = (section: ShopSectionEntity) =>
  section.sectionType === "PRODUCT_LIST" ? section.title || SECTION_TYPE_LABELS.PRODUCT_LIST : SECTION_TYPE_LABELS[section.sectionType];

export const MainSectionsSettings = (props: MainSectionsSettingsProps) => {
  const { sections, onReorder, expandedSectionId, onExpand, isPersistable, onCreateProductList } = props;
  const [isCreating, setIsCreating] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // 드래그 결과를 그대로 displayOrder 로 다시 매긴다 — 화면 순서가 곧 데이터다
  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = sections.findIndex((section) => `section-${section.id}` === active.id);
    const to = sections.findIndex((section) => `section-${section.id}` === over.id);
    if (from < 0 || to < 0) return;
    onReorder(renumberSections(arrayMove(sections, from, to)));
  };

  return (
    <SettingsCard
      title="메인 화면 구성"
      aside={`${sections.filter((section) => section.isEnabled).length}/${sections.length}개 사용`}
      description="쇼핑몰 메인에 보이는 순서입니다. 끌어서 순서를 바꾸고, 눌러서 세부 설정을 엽니다."
    >
      {!isPersistable ? <Notice tone="warn">섹션 정보를 불러오지 못했습니다. 미리 볼 수는 있지만 섹션 변경은 저장되지 않습니다.</Notice> : null}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sections.map((section) => `section-${section.id}`)} strategy={verticalListSortingStrategy}>
          <div className="flex flex-col gap-[0.375rem]">
            {sections.map((section) => (
              <SectionRow
                key={section.id}
                {...props}
                section={section}
                isExpanded={expandedSectionId === section.id}
                onToggleExpand={() => onExpand(expandedSectionId === section.id ? null : section.id)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <button
        type="button"
        disabled={!isPersistable || isCreating}
        onClick={async () => {
          setIsCreating(true);
          try {
            await onCreateProductList();
          } finally {
            setIsCreating(false);
          }
        }}
        className="flex flex-row items-center justify-center gap-[0.25rem] h-[2.5rem] rounded-[8px] border border-dashed border-[#C9D6F2]
          text-[13px] text-[#4285F4] hover:bg-[#F5F8FF] disabled:text-[#BBBBBB] disabled:border-[#E0E0E0] disabled:hover:bg-transparent"
      >
        <LucidePlus className="w-4 h-4" />
        {isCreating ? "추가하는 중..." : "상품 리스트 섹션 추가"}
      </button>
    </SettingsCard>
  );
};

/* ------------------------------------------------------------------ *
 * 섹션 한 줄 (접힘: 이름 · 요약 · 스위치 / 펼침: 섹션별 설정)
 * ------------------------------------------------------------------ */

interface SectionRowProps extends MainSectionsSettingsProps {
  section: ShopSectionEntity;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

const SectionRow = (props: SectionRowProps) => {
  const { section, isExpanded, onToggleExpand, onToggleEnabled } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `section-${section.id}` });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className={`rounded-[8px] border ${isExpanded ? "border-[#4285F4] shadow-[0_0_0_3px_rgba(66,133,244,0.12)]" : "border-[#EEEEEE]"} bg-white`}
    >
      <div className="flex flex-row items-center gap-[0.5rem] px-[0.625rem] py-[0.5rem]">
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label={`${sectionLabel(section)} 순서 바꾸기`}
          className="shrink-0 text-[#BBBBBB] hover:text-[#555] cursor-grab active:cursor-grabbing"
        >
          <DragHandleIcon />
        </button>
        <button type="button" onClick={onToggleExpand} aria-expanded={isExpanded} className="flex-1 min-w-0 flex flex-row items-center gap-[0.5rem] text-start">
          <span className={`truncate text-[13px] font-medium ${section.isEnabled ? "text-[#252525]" : "text-[#AAAAAA]"}`}>{sectionLabel(section)}</span>
          <span className="shrink-0 text-[12px] text-[#999]">{summarize(props)}</span>
          <LucideChevronDown className={`ml-auto shrink-0 w-4 h-4 text-[#999] transition-transform ${isExpanded ? "rotate-180" : ""}`} />
        </button>
        <ToggleSwitch size="sm" checked={section.isEnabled} onChange={() => onToggleEnabled(section)} label={`${sectionLabel(section)} 사용`} />
      </div>
      {isExpanded ? (
        <div className="flex flex-col gap-[0.875rem] px-[0.75rem] pb-[0.875rem] pt-[0.25rem] border-t border-[#F0F0F0]">
          <SectionBody {...props} />
        </div>
      ) : null}
    </div>
  );
};

/** 접힌 줄에 보일 한 단어 요약 */
const summarize = ({ section, bannersData, shopEntityData, productsData }: SectionRowProps): string => {
  switch (section.sectionType) {
    case "BANNER":
      return `${bannersData?.length ?? 0}장 · ${shopEntityData?.isCarouselLayout ? "캐러셀" : "전체 영역"}`;
    case "STRIP_BANNER":
      return shopEntityData?.stripBannerUrl ? "이미지 있음" : "이미지 없음";
    case "RECENTLY_VIEWED":
      return "로그인 방문자";
    case "BEST_SELLER":
      return `${productsData?.filter((product) => product.isBestSeller).length ?? 0}개`;
    case "ALL_PRODUCTS":
      return `${section.columnCount}열`;
    case "PORTFOLIO":
      return SECTION_LAYOUT_TYPE_LABELS[section.layoutType];
    case "PRODUCT_LIST":
      return `상품 ${section.products.length}개`;
    default:
      return "";
  }
};

/** 고른 상품이 쇼핑몰에 보이지 않을 때(판매 중지 · 숨긴 카테고리) 붙이는 표시 */
const HiddenOnShopBadge = ({ productId, visibleProductIds }: { productId: number; visibleProductIds: Set<number> | null }) =>
  visibleProductIds && !visibleProductIds.has(productId) ? (
    <span className="shrink-0 px-[0.375rem] rounded-[4px] bg-[#FFF4E5] text-[11px] text-[#B4560A]">쇼핑몰 미노출</span>
  ) : null;

const ColumnCountField = ({ value, onChange, hint }: { value: number; onChange: (columnCount: number) => void; hint?: string }) => (
  <Field label="열 수" hint={hint}>
    <Segmented
      ariaLabel="열 수"
      value={value}
      options={SECTION_COLUMN_COUNT_OPTIONS.map((count) => ({ value: count as number, label: `${count}열` }))}
      onChange={onChange}
    />
  </Field>
);

const SectionBody = (props: SectionRowProps) => {
  const { section, onPatchSection, shopEntityData, setShopEntityData } = props;
  const patch = (values: Partial<ShopSectionEntity>) => onPatchSection(section.id, values);
  const updateShop = (values: Partial<ShopEntity>) => setShopEntityData((original) => (original ? { ...original, ...values } : original));

  switch (section.sectionType) {
    case "BANNER":
      return (
        <>
          <Field label="배치">
            <Segmented
              ariaLabel="배너 배치"
              value={shopEntityData?.isCarouselLayout ? "CAROUSEL" : "FULL"}
              options={[
                { value: "FULL", label: "전체 영역" },
                { value: "CAROUSEL", label: "캐러셀" },
              ]}
              onChange={(value) => updateShop({ isCarouselLayout: value === "CAROUSEL" })}
            />
          </Field>
          <Field label="넘김 간격">
            <Segmented
              ariaLabel="배너 넘김 간격"
              value={shopEntityData?.designProductSlideInterval ?? 5}
              options={[
                { value: 2, label: "2초" },
                { value: 5, label: "5초" },
                { value: 9, label: "9초" },
              ]}
              onChange={(designProductSlideInterval) => updateShop({ designProductSlideInterval })}
            />
          </Field>
          <Field label={`배너 이미지 (${props.bannersData?.length ?? 0}장)`} hint="배너 추가 · 수정은 창에서 바로 저장됩니다.">
            <div className="grid grid-cols-2 gap-[0.5rem]">
              {(props.bannersData ?? []).map((banner) => (
                <button
                  key={banner.id}
                  type="button"
                  onClick={() => props.onEditBanner(banner.id)}
                  aria-label={`${banner.title || "배너"} 수정`}
                  className="group relative aspect-[730/380] rounded-[6px] overflow-hidden border border-[#EEEEEE]"
                >
                  <img className="w-full h-full object-cover" src={banner.bannerUrl} alt="" />
                  <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/35 transition-colors">
                    <LucidePencilLine className="w-5 h-5 text-white opacity-0 group-hover:opacity-100" />
                  </span>
                </button>
              ))}
              <button
                type="button"
                onClick={props.onAddBanner}
                className="aspect-[730/380] rounded-[6px] border border-dashed border-[#C9D6F2] text-[13px] text-[#4285F4]
                  flex flex-col items-center justify-center gap-[0.25rem] hover:bg-[#F5F8FF]"
              >
                <LucidePlus className="w-4 h-4" />
                배너 추가
              </button>
            </div>
          </Field>
        </>
      );

    case "STRIP_BANNER": {
      const imageUrl = props.stripBannerPreviewUrl ?? shopEntityData?.stripBannerUrl ?? null;
      return (
        <>
          <Field label="이미지" hint="권장 1200 × 120px. 새 이미지는 [저장]할 때 올라갑니다.">
            <label className="relative block h-[72px] rounded-[6px] overflow-hidden border border-dashed border-[#D9D9D9] cursor-pointer hover:border-[#4285F4]">
              {imageUrl ? (
                <img className="w-full h-full object-cover" src={imageUrl} alt="띠배너" />
              ) : (
                <span className="flex h-full items-center justify-center text-[13px] text-[#AAAAAA]">이미지를 고르세요</span>
              )}
              <input
                type="file"
                className="hidden"
                accept=".png,.jpg,.jpeg,.gif,.webp"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) props.onPickStripBanner(file);
                  event.target.value = "";
                }}
              />
            </label>
          </Field>
          <Field label="눌렀을 때 이동할 주소 (선택)">
            <TextField
              value={shopEntityData?.stripBannerLink ?? ""}
              placeholder="https://example.com"
              onChange={(event) => updateShop({ stripBannerLink: event.target.value || null })}
            />
          </Field>
          {shopEntityData?.stripBannerUrl ? (
            <button
              type="button"
              onClick={() => {
                if (window.confirm("저장된 띠배너 이미지를 삭제할까요? 바로 반영됩니다.")) void props.onDeleteStripBanner();
              }}
              className="self-start text-[12px] text-[#F24A4A] hover:underline"
            >
              저장된 이미지 삭제
            </button>
          ) : null}
        </>
      );
    }

    case "RECENTLY_VIEWED":
      return <Notice>로그인한 방문자가 최근에 본 상품이 있을 때만 보입니다. 미리보기(로그인 전 화면)에는 나오지 않습니다.</Notice>;

    case "BEST_SELLER": {
      const bestSellers = (props.productsData ?? []).filter((product) => product.isBestSeller);
      return (
        <Field label={`상품 (${bestSellers.length}/5)`} hint="쇼핑몰 미노출 상품은 고른 목록에 있어도 쇼핑몰에 보이지 않습니다.">
          {bestSellers.length > 0 ? (
            <ol className="flex flex-col gap-[0.375rem]">
              {bestSellers.map((product, index) => (
                <li key={product.id} className="flex flex-row items-center gap-[0.5rem] text-[13px]">
                  <span className="w-[1.25rem] text-center text-[12px] font-bold text-[#4285F4]">{index + 1}</span>
                  {product.thumbnail ? (
                    <img src={product.thumbnail} alt="" className="w-[2rem] h-[2rem] rounded-[4px] object-cover bg-[#F3F4F6]" />
                  ) : (
                    <span className="w-[2rem] h-[2rem] rounded-[4px] bg-[#F3F4F6]" />
                  )}
                  <span className="truncate">{product.name}</span>
                  <HiddenOnShopBadge productId={product.id} visibleProductIds={props.visibleProductIds} />
                </li>
              ))}
            </ol>
          ) : (
            <div className="text-[13px] text-[#AAAAAA]">고른 상품이 없습니다.</div>
          )}
          <button
            type="button"
            onClick={props.onEditBestSellers}
            className="h-[2.25rem] rounded-[8px] border border-[#E0E0E0] text-[13px] hover:bg-[#FAFAFA]"
          >
            상품 고르기
          </button>
        </Field>
      );
    }

    case "ALL_PRODUCTS":
      return (
        <>
          <SectionTextFields title={section.title} subtitle={section.subtitle} titlePlaceholder="전체 상품" onChange={patch} />
          <ColumnCountField
            value={section.columnCount}
            onChange={(columnCount) => patch({ columnCount })}
            hint={`PC 는 ${section.columnCount}열 × ${SECTION_GRID_ROWS}행(${section.columnCount * SECTION_GRID_ROWS}개)씩 넘깁니다. 모바일은 2열입니다.`}
          />
        </>
      );

    case "PORTFOLIO":
      return (
        <>
          <Notice>메인 블록만 켜고 끕니다. 헤더 메뉴의 포트폴리오 노출은 포트폴리오 관리에서 설정합니다.</Notice>
          <SectionTextFields title={section.title} subtitle={section.subtitle} titlePlaceholder="포트폴리오" onChange={patch} />
          <Field label="표시 형식">
            <Segmented
              ariaLabel="포트폴리오 표시 형식"
              value={section.layoutType}
              options={[
                { value: "GRID", label: "그리드" },
                { value: "LIST", label: "리스트" },
              ]}
              onChange={(layoutType) => patch({ layoutType })}
            />
          </Field>
          {/* LIST 는 한 줄에 하나라 열 수를 쓰지 않는다 */}
          {isColumnCountMeaningful(section.layoutType) ? (
            <ColumnCountField value={section.columnCount} onChange={(columnCount) => patch({ columnCount })} hint="화면이 좁아지면 열 수가 줄고, 600px 미만은 1열입니다." />
          ) : null}
        </>
      );

    case "PRODUCT_LIST":
      return <ProductListBody {...props} />;

    default:
      return null;
  }
};

const ProductListBody = (props: SectionRowProps) => {
  const { section, onPatchSection, productsData, isPersistable, onDeleteProductList } = props;
  const [isPickerVisible, setIsPickerVisible] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const patch = (values: Partial<ShopSectionEntity>) => onPatchSection(section.id, values);

  return (
    <>
      <SectionProductPickerModal
        setIsModalVisible={setIsPickerVisible}
        isModalVisible={isPickerVisible}
        sectionTitle={section.title || "상품 리스트"}
        productsData={productsData}
        selectedProducts={section.products}
        onChange={(products) => patch({ products })}
      />
      <SectionTextFields
        title={section.title}
        subtitle={section.subtitle}
        titlePlaceholder="인기 상품"
        subtitlePlaceholder="가장 많이 찾는 상품을 모았습니다"
        onChange={patch}
      />
      <Field label="카드 모양">
        <Segmented
          ariaLabel="카드 모양"
          value={section.cardShape}
          options={[
            { value: "DEFAULT", label: "기본형" },
            { value: "CIRCLE", label: "원형" },
          ]}
          onChange={(cardShape) => patch({ cardShape })}
        />
      </Field>
      <ColumnCountField value={section.columnCount} onChange={(columnCount) => patch({ columnCount })} />
      <Field label={`상품 (${section.products.length}개)`}>
        {section.products.length > 0 ? (
          <div className="flex flex-row flex-wrap gap-[0.25rem]">
            {section.products.map((product) => (
              <span key={product.id} className="flex flex-row items-center gap-[0.25rem] px-[0.5rem] py-[0.125rem] rounded-full bg-[#F3F4F6] text-[12px] text-[#555]">
                {product.name}
                <HiddenOnShopBadge productId={product.id} visibleProductIds={props.visibleProductIds} />
              </span>
            ))}
          </div>
        ) : null}
        <button
          type="button"
          onClick={() => setIsPickerVisible(true)}
          className="h-[2.25rem] rounded-[8px] border border-[#E0E0E0] text-[13px] hover:bg-[#FAFAFA]"
        >
          상품 고르기
        </button>
      </Field>
      <button
        type="button"
        disabled={!isPersistable || isDeleting}
        onClick={async () => {
          if (!window.confirm("이 상품 리스트 섹션을 삭제할까요? 바로 반영됩니다.")) return;
          setIsDeleting(true);
          try {
            await onDeleteProductList(section);
          } finally {
            setIsDeleting(false);
          }
        }}
        className="self-start text-[12px] text-[#F24A4A] hover:underline disabled:text-[#BBBBBB]"
      >
        {isDeleting ? "삭제하는 중..." : "이 섹션 삭제"}
      </button>
    </>
  );
};
